import { useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { Pencil, Trash2, Check, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { Attribute } from '@/types/attribute'

ModuleRegistry.registerModules([AllCommunityModule])

function TextCell(props: CustomCellRendererProps<Attribute>) {
  const value =
    typeof props.value === 'string' && props.value.trim().length > 0
      ? props.value
      : '-'

  return <span className="text-sm text-foreground line-clamp-2">{value}</span>
}

function BooleanCell(props: CustomCellRendererProps<Attribute>) {
  const val = props.value as boolean | null | undefined

  if (typeof val !== 'boolean') {
    return <span className="text-sm text-muted-foreground">-</span>
  }

  return val ? (
    <Check className="h-4 w-4 text-green-600" />
  ) : (
    <X className="h-4 w-4 text-muted-foreground" />
  )
}

function ValuesCell(props: CustomCellRendererProps<Attribute>) {
  const values = props.value as string[]
  if (!values || values.length === 0)
    return <span className="text-sm text-muted-foreground">-</span>

  return (
    <Badge variant="secondary" className="text-xs">
      {values.length} {values.length === 1 ? 'value' : 'values'}
    </Badge>
  )
}

interface ActionsCellProps extends CustomCellRendererProps<Attribute> {
  onEdit: (attribute: Attribute) => void
  onDelete: (attribute: Attribute) => void
}

function ActionsCell(props: ActionsCellProps) {
  const attribute = props.data
  if (!attribute) return null

  const { onEdit, onDelete } = props

  return (
    <div className="flex items-center justify-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 cursor-pointer"
        onClick={() => onEdit(attribute)}
      >
        <Pencil className="h-4 w-4 text-muted-foreground" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 cursor-pointer"
        onClick={() => onDelete(attribute)}
      >
        <Trash2 className="h-4 w-4 text-muted-foreground" />
      </Button>
    </div>
  )
}

interface AttributesGridProps {
  attributes: Attribute[]
  onEdit: (attribute: Attribute) => void
  onDelete: (attribute: Attribute) => void
}

export function AttributesGrid({
  attributes,
  onEdit,
  onDelete,
}: AttributesGridProps) {
  const columnDefs: ColDef<Attribute>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 2,
        minWidth: 150,
        cellRenderer: TextCell,
      },
      {
        field: 'category',
        headerName: 'Category',
        flex: 1,
        minWidth: 120,
        cellRenderer: TextCell,
      },
      {
        field: 'subcategory',
        headerName: 'Subcategory',
        flex: 1,
        minWidth: 120,
        cellRenderer: TextCell,
      },
      {
        field: 'required',
        headerName: 'Required',
        flex: 1,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: BooleanCell,
      },
      {
        field: 'locked',
        headerName: 'Locked',
        flex: 1,
        minWidth: 90,
        maxWidth: 110,
        cellRenderer: BooleanCell,
      },
      {
        field: 'defined_values',
        headerName: 'Values',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: ValuesCell,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 100,
        maxWidth: 120,
        cellClass: 'ag-center-aligned-cell',
        headerClass: 'ag-center-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onEdit,
          onDelete,
        },
      },
    ],
    [onEdit, onDelete]
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <div className="w-full">
      <div className="border border-[#e7e7e7] bg-white overflow-hidden">
        <div className="ag-theme-michaels w-full">
          <AgGridReact<Attribute>
            rowData={attributes}
            columnDefs={columnDefs}
            theme="legacy"
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={48}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={onGridSizeChanged}
            overlayNoRowsTemplate="<span>No attributes found</span>"
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
          />
        </div>
      </div>
    </div>
  )
}
